import { z } from "zod";
import type { Role } from "./enums.js";

/**
 * Modules (docs/ASSETS-MJOP.md, "Modules en rechten"): een organisatie neemt modules af, een
 * gebruiker heeft per module een niveau. Gebouwen is de basis en altijd actief.
 */
export const MODULES = [
  "buildings",
  "issues",
  "assets",
  "mjop",
  "condition",
  "meters",
  "directory",
  "intake",
  "dossier",
  "materials",
  "contracts",
  "visits",
] as const;
export type ModuleKey = (typeof MODULES)[number];
export const MODULE_LABELS: Record<ModuleKey, string> = {
  buildings: "Gebouwen",
  issues: "Bevindingen",
  assets: "Assets",
  mjop: "MJOP",
  condition: "Conditiemeting",
  meters: "Meterstanden",
  directory: "Adresboek",
  intake: "Inbox",
  dossier: "Dossier",
  materials: "Materialen",
  contracts: "Contracten",
  visits: "Bezoek",
};
export const MODULE_DESCRIPTIONS: Record<ModuleKey, string> = {
  buildings: "Gebouwen, plattegronden, basis",
  issues: "Bevindingen, taken, rapport, raming herstelkosten",
  assets: "Assets, QR-codes, verplichtingen en keuringen",
  mjop: "Begroting, dashboard, kosten bij verplichtingen",
  condition: "Conditiemeting volgens NEN 2767: metingen, gebrekenlijsten, scores",
  meters: "Meters, opnamerondes met foto, controle op afwijkingen, Excel",
  directory: "Leveranciers, huurders en contactpersonen, erkenningen, storingsnummers",
  intake: "Inbox: meldingen van buiten (QR-code, app) beoordelen en omzetten in bevindingen",
  dossier: "Documenten per gebouw: soorten, versies, vervaldatums, tekeningen",
  materials: "Artikellijst, reservevoorraad in het gebouw, materialen bij onderhoud en taken, afboeken",
  contracts: "Onderhoudscontracten met leveranciers: posten, prijzen, gedekte uitvoeringen, zonder contract",
  visits: "Bezoek aankondigen en goedkeuren, wie er binnen is, aanwezigheid per verdieping voor de BHV",
};

export const MODULE_LEVELS = ["none", "view", "edit"] as const;
export type ModuleLevel = (typeof MODULE_LEVELS)[number];
export const MODULE_LEVEL_LABELS: Record<ModuleLevel, string> = { none: "Geen", view: "Bekijken", edit: "Bewerken" };
const RANK: Record<ModuleLevel, number> = { none: 0, view: 1, edit: 2 };
export const levelAtLeast = (have: ModuleLevel, need: ModuleLevel) => RANK[have] >= RANK[need];
export const minLevel = (a: ModuleLevel, b: ModuleLevel): ModuleLevel => (RANK[a] <= RANK[b] ? a : b);

/** Stand van een module bij een organisatie. Proef verlopen = alleen bekijken. */
export const ORG_MODULE_STATUSES = ["active", "trial", "off"] as const;
export type OrgModuleStatus = (typeof ORG_MODULE_STATUSES)[number];
export const ORG_MODULE_STATUS_LABELS: Record<OrgModuleStatus, string> = {
  active: "Aan",
  trial: "Proef",
  off: "Uit",
};

/** Zonder eigen rij: Gebouwen, Bevindingen en Assets aan (zoals bij de invoering), de rest uit. */
export const DEFAULT_ORG_MODULES: Record<ModuleKey, OrgModuleStatus> = {
  buildings: "active",
  issues: "active",
  assets: "active",
  mjop: "off",
  condition: "off",
  meters: "off",
  // Planning, werkbonnen en contracten kiezen hieruit hun leverancier: standaard aan (besluit 23-10).
  directory: "active",
  intake: "active",
  // Asset-bijlagen gingen op in het dossier (docs/DOSSIER.md): standaard aan.
  dossier: "active",
  materials: "off",
  contracts: "off",
  visits: "off",
};
export const MODULE_TRIAL_DAYS = 30;

/** Hoogste niveau dat de organisatie toelaat voor een module. */
export function orgModuleCap(
  module: ModuleKey,
  row: { status: OrgModuleStatus; trialEndsAt: Date | string | null } | undefined,
  now = new Date(),
): ModuleLevel {
  if (module === "buildings") return "edit";
  const status = row?.status ?? DEFAULT_ORG_MODULES[module];
  if (status === "active") return "edit";
  if (status === "off") return "none";
  const end = row?.trialEndsAt ? new Date(row.trialEndsAt) : null;
  return !end || end.getTime() > now.getTime() ? "edit" : "view";
}

/**
 * Niveau per rol zonder eigen instelling (voorinvulling bij uitnodigen). Eigenaar en beheerder
 * hebben altijd bewerken. Medewerkers (inspecteur) bewerken Gebouwen, Bevindingen en Assets;
 * de uitvoerder werkt bevindingen af; externen (klant) bekijken. MJOP standaard nergens. Meldingen
 * beoordelen zet de beheerder per collega aan, ook voor een medewerker van de klant (issue #136).
 */
export const ROLE_MODULE_DEFAULTS: Record<Role, Record<ModuleKey, ModuleLevel>> = {
  owner: {
    buildings: "edit",
    issues: "edit",
    assets: "edit",
    mjop: "edit",
    condition: "edit",
    meters: "edit",
    directory: "edit",
    intake: "edit",
    dossier: "edit",
    materials: "edit",
    contracts: "edit",
    visits: "edit",
  },
  beheerder: {
    buildings: "edit",
    issues: "edit",
    assets: "edit",
    mjop: "edit",
    condition: "edit",
    meters: "edit",
    directory: "edit",
    intake: "edit",
    dossier: "edit",
    materials: "edit",
    contracts: "edit",
    visits: "edit",
  },
  inspecteur: {
    buildings: "edit",
    issues: "edit",
    assets: "edit",
    mjop: "none",
    condition: "edit",
    meters: "edit",
    directory: "edit",
    intake: "none",
    dossier: "edit",
    materials: "edit",
    contracts: "view",
    visits: "edit",
  },
  uitvoerder: {
    buildings: "view",
    issues: "edit",
    assets: "view",
    mjop: "none",
    condition: "none",
    meters: "none",
    directory: "view",
    intake: "none",
    dossier: "view",
    materials: "view",
    contracts: "none",
    visits: "view",
  },
  klant: {
    buildings: "view",
    issues: "view",
    assets: "view",
    mjop: "none",
    condition: "none",
    meters: "view",
    directory: "none",
    intake: "none",
    dossier: "view",
    materials: "none",
    contracts: "view",
    visits: "none",
  },
};
export const ALWAYS_EDIT_ROLES: ReadonlySet<Role> = new Set(["owner", "beheerder"]);

/** Niveau van een gebruiker: eigen instelling of rolstandaard, begrensd door de organisatie. */
export function effectiveModules(
  role: Role,
  orgRows: { module: ModuleKey; status: OrgModuleStatus; trialEndsAt: Date | string | null }[],
  userRows: { module: ModuleKey; level: ModuleLevel }[],
  now = new Date(),
): Record<ModuleKey, ModuleLevel> {
  const out = {} as Record<ModuleKey, ModuleLevel>;
  for (const m of MODULES) {
    const cap = orgModuleCap(
      m,
      orgRows.find((r) => r.module === m),
      now,
    );
    const own = ALWAYS_EDIT_ROLES.has(role)
      ? "edit"
      : (userRows.find((r) => r.module === m)?.level ?? ROLE_MODULE_DEFAULTS[role][m]);
    out[m] = minLevel(cap, own);
  }
  return out;
}

export const memberModulesBodySchema = z.object({
  levels: z.partialRecord(z.enum(MODULES), z.enum(MODULE_LEVELS)),
});
export const platformOrgModulesBodySchema = z.object({
  modules: z
    .array(
      z.object({
        module: z.enum(MODULES),
        status: z.enum(ORG_MODULE_STATUSES),
        trialEndsAt: z.iso.date().nullable().optional(),
        pricePerBuildingCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
      }),
    )
    .max(MODULES.length),
});

export interface OrgModuleDto {
  module: ModuleKey;
  status: OrgModuleStatus;
  trialEndsAt: string | null;
  pricePerBuildingCents: number | null;
  /** Wat de organisatie nu kan: bewerken, alleen bekijken (proef verlopen) of niets. */
  cap: ModuleLevel;
}

/**
 * Welke module (en welk niveau) een permissie standaard vraagt (docs/ASSETS-MJOP.md, Modules en
 * rechten). Een route kan dat met `config.module` preciezer opgeven, bv. lezen van assets.
 * `null` = niet aan een module gebonden (gebruikersbeheer, instellingen).
 */
export const PERMISSION_MODULES: Record<string, readonly [ModuleKey, ModuleLevel] | null> = {
  "building.view": ["buildings", "view"],
  "building.manage": ["buildings", "edit"],
  "plan.upload": ["buildings", "edit"],
  "issue.create": ["issues", "edit"],
  "issue.edit": ["issues", "edit"],
  "issue.delete": ["issues", "edit"],
  "issue.status": ["issues", "edit"],
  // Reageren mag ook met bekijken (een klant plaatst opmerkingen).
  "issue.comment": ["issues", "view"],
  "issue.photo": ["issues", "edit"],
  "task.manage": ["issues", "edit"],
  "robaws.send": ["issues", "edit"],
  "robaws.link": ["issues", "edit"],
  "report.create": ["issues", "view"],
  "report.view": ["issues", "view"],
  "settings.robaws": null,
  "users.manage": null,
  "asset.manage": ["assets", "edit"],
  "asset.delete": ["assets", "edit"],
  // Inbox Te beoordelen: lezen met bekijken, omzetten/koppelen/afwijzen met bewerken (route geeft het niveau).
  "intake.triage": ["intake", "edit"],
  "document.manage": ["dossier", "edit"],
  "document.delete": ["dossier", "edit"],
  "document.types": ["dossier", "edit"],
  "material.manage": ["materials", "edit"],
  // Besteld en Geleverd zetten: alleen beheerders (besluit 26-10, issue #173).
  "material.order": ["materials", "edit"],
  "contract.manage": ["contracts", "edit"],
  // Bezoek aankondigen, binnen melden en uitklokken; goedkeuren alleen als gastheer of beheerder (docs/BEZOEK.md).
  "visit.manage": ["visits", "edit"],
};
